import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import type { FormInstance } from 'antd';
import { formatMoney } from '@/utils/format';
import { isWbsLeafNode, WBS_COST_ATTACHMENT_LAYER } from '@/utils/est3WbsLayers';
import { EpcEstimateWbsTreeSelect } from './EpcEstimateWbsTreeSelect';

const { Text } = Typography;

const RATE_SOURCES = [
  'MANUAL',
  'CATALOGUE',
  'LAST_PURCHASE',
  'SUPPLIER_QUOTE',
  'INDEXED',
  'HISTORICAL',
] as const;

export interface EpcEstimateCostLineAddDrawerProps {
  open: boolean;
  onClose: () => void;
  form: FormInstance;
  readOnly?: boolean;
  wbsOptions: Array<{ id: string; code: string; name?: string; parent_id?: string | null }>;
  cbsOptions: Array<{ id: string; code: string; name?: string; parent_id?: string | null }>;
  boqOptions: Array<{
    id: string;
    item_number?: string;
    description?: string;
    schedule_amount?: number;
  }>;
  costLines?: Array<Record<string, unknown>>;
  kindFilter?: 'MATERIAL' | 'LABOUR' | 'EQUIPMENT';
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>;
  pending?: boolean;
  defaultBoqLineId?: string | null;
  requireBoq?: boolean;
  onSetupWbs?: () => void;
}

export const EpcEstimateCostLineAddDrawer: React.FC<EpcEstimateCostLineAddDrawerProps> = ({
  open,
  onClose,
  form,
  readOnly,
  wbsOptions,
  cbsOptions,
  boqOptions,
  costLines = [],
  kindFilter,
  onSubmit,
  pending,
  defaultBoqLineId,
  requireBoq,
  onSetupWbs,
}) => {
  const [qty, setQty] = useState(1);
  const [rate, setRate] = useState(0);
  const [waste, setWaste] = useState<number | undefined>();
  const [productivity, setProductivity] = useState<number | undefined>();
  const boqId = Form.useWatch('clientBoqLineId', form);
  const wbsNodeId = Form.useWatch('wbsNodeId', form);
  const watchedResourceKind = Form.useWatch('resourceKind', form);
  const resourceKind = kindFilter || watchedResourceKind || 'MATERIAL';

  const boqLine = useMemo(
    () => boqOptions.find((b) => b.id === (boqId || defaultBoqLineId)),
    [boqOptions, boqId, defaultBoqLineId]
  );

  const smartDefaults = useMemo(() => {
    const targetBoq = defaultBoqLineId || boqId;
    if (!targetBoq) return null;
    const linked = costLines.filter((c) => c.client_boq_line_id === targetBoq);
    if (!linked.length) return null;
    const latest = linked[linked.length - 1];
    return {
      wbsNodeId: latest.wbs_node_id as string | undefined,
      cbsCodeId: latest.cbs_code_id as string | undefined,
    };
  }, [costLines, defaultBoqLineId, boqId]);

  useEffect(() => {
    if (open) {
      form.resetFields();
      const defaults: Record<string, unknown> = {
        quantity: 1,
        rate: 0,
        rateSource: 'MANUAL',
        resourceKind: kindFilter || 'MATERIAL',
        clientBoqLineId: defaultBoqLineId || undefined,
      };
      if (smartDefaults?.wbsNodeId) defaults.wbsNodeId = smartDefaults.wbsNodeId;
      if (smartDefaults?.cbsCodeId) defaults.cbsCodeId = smartDefaults.cbsCodeId;
      if (boqLine?.description) defaults.description = boqLine.description;
      form.setFieldsValue(defaults);
      setQty(1);
      setRate(0);
      setWaste(undefined);
      setProductivity(undefined);
    }
  }, [open, defaultBoqLineId, kindFilter, form, smartDefaults, boqLine?.description]);

  const effectiveQty = resourceKind === 'LABOUR' && productivity ? qty / productivity : qty;
  const effectiveRate = resourceKind === 'MATERIAL' && waste ? rate * (1 + waste / 100) : rate;
  const liveTotal = effectiveQty * effectiveRate;

  const wbsEmpty = wbsOptions.length === 0;
  const selectedWbs = useMemo(
    () => wbsOptions.find((n) => n.id === wbsNodeId),
    [wbsOptions, wbsNodeId]
  );
  const wbsNotLeaf =
    wbsNodeId && !isWbsLeafNode(String(wbsNodeId), wbsOptions as any[]);
  const wbsNotPackageLayer =
    selectedWbs &&
    (selectedWbs as any).layer_code &&
    String((selectedWbs as any).layer_code) !== WBS_COST_ATTACHMENT_LAYER;

  return (
    <Drawer
      title="Add cost line"
      open={open}
      onClose={onClose}
      width={520}
      destroyOnClose
      extra={
        !readOnly ? (
          <Button type="primary" loading={pending} onClick={() => form.submit()}>
            Add
          </Button>
        ) : null
      }
    >
      {boqLine ? (
        <Card size="small" style={{ marginBottom: 16 }} title="BOQ context">
          <Space direction="vertical" size={4}>
            <Text strong>{boqLine.item_number || '—'}</Text>
            <Text type="secondary">{boqLine.description}</Text>
            {boqLine.schedule_amount != null ? (
              <Tag color="blue">
                Schedule {formatMoney(boqLine.schedule_amount, { display: 'plain', maximumFractionDigits: 0 })}
              </Tag>
            ) : null}
          </Space>
        </Card>
      ) : null}

      {wbsEmpty && !readOnly ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No WBS nodes on this revision"
          description={
            <Space direction="vertical">
              <Text type="secondary">Import or copy a WBS structure before adding cost lines.</Text>
              {onSetupWbs ? (
                <Button size="small" type="primary" onClick={onSetupWbs}>
                  Set up WBS
                </Button>
              ) : null}
            </Space>
          }
        />
      ) : null}

      <Form
        form={form}
        layout="vertical"
        disabled={readOnly}
        onValuesChange={(_, all) => {
          if (all.quantity != null) setQty(Number(all.quantity) || 0);
          if (all.rate != null) setRate(Number(all.rate) || 0);
          if (all.wasteFactor != null) setWaste(Number(all.wasteFactor));
          if (all.productivityFactor != null) setProductivity(Number(all.productivityFactor));
        }}
        onFinish={async (v) => {
          await onSubmit({
            ...v,
            resourceKind: kindFilter || v.resourceKind || 'MATERIAL',
            rateSource: v.rateSource || 'MANUAL',
          });
          onClose();
        }}
      >
        <Typography.Title level={5} style={{ marginTop: 0 }}>
          Classification
        </Typography.Title>
        <Form.Item
          name="clientBoqLineId"
          label="Client BOQ"
          rules={requireBoq || defaultBoqLineId ? [{ required: true }] : undefined}
        >
          <Select
            allowClear={!requireBoq}
            showSearch
            placeholder="Link to BOQ line"
            optionFilterProp="label"
            options={boqOptions.map((n) => ({
              value: n.id,
              label: `${n.item_number || ''} ${n.description || ''}`.trim() || n.id.slice(0, 8),
            }))}
          />
        </Form.Item>
        <Form.Item name="description" label="Description" rules={[{ required: true }]}>
          <Input placeholder="Cost line description" />
        </Form.Item>
        <Form.Item name="wbsNodeId" label="WBS" rules={[{ required: true }]}>
          <EpcEstimateWbsTreeSelect nodes={wbsOptions} disabled={wbsEmpty} />
        </Form.Item>
        {wbsNotLeaf ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Selected WBS node has children"
            description="Attach costs at the deepest Package level where possible for accurate roll-ups."
          />
        ) : null}
        {wbsNotPackageLayer && !wbsNotLeaf ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Layer is ${(selectedWbs as any).layer_code} — recommended: ${WBS_COST_ATTACHMENT_LAYER}`}
          />
        ) : null}
        <Form.Item name="cbsCodeId" label="CBS" rules={[{ required: true }]}>
          <EpcEstimateWbsTreeSelect nodes={cbsOptions} kind="cbs" />
        </Form.Item>
        {!kindFilter ? (
          <Form.Item name="resourceKind" label="Resource kind" initialValue="MATERIAL">
            <Select
              options={['MATERIAL', 'LABOUR', 'EQUIPMENT', 'SUBCONTRACT', 'OTHER'].map((v) => ({
                value: v,
                label: v,
              }))}
            />
          </Form.Item>
        ) : null}

        <Typography.Title level={5}>Economics</Typography.Title>
        <Space style={{ width: '100%' }} size="middle">
          <Form.Item name="quantity" label="Quantity" initialValue={1} style={{ flex: 1 }}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="uom" label="UoM" style={{ flex: 1 }}>
            <Input placeholder="e.g. m³" />
          </Form.Item>
        </Space>
        <Form.Item name="rate" label="Rate" initialValue={0}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        {resourceKind === 'MATERIAL' ? (
          <Form.Item name="wasteFactor" label="Waste factor (%)" tooltip="Applied to rate preview">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
        ) : null}
        {resourceKind === 'LABOUR' ? (
          <Form.Item name="productivityFactor" label="Productivity factor" tooltip="Qty ÷ factor for total">
            <InputNumber min={0.01} style={{ width: '100%' }} />
          </Form.Item>
        ) : null}
        <Card size="small" type="inner">
          <Text type="secondary">Live total</Text>
          <div>
            <Text strong style={{ fontSize: 18 }}>
              {formatMoney(liveTotal, { display: 'plain', maximumFractionDigits: 2 })}
            </Text>
            {(waste || productivity) && (
              <Text type="secondary" style={{ marginLeft: 8 }}>
                ({effectiveQty.toFixed(2)} × {formatMoney(effectiveRate, { display: 'plain', maximumFractionDigits: 2 })})
              </Text>
            )}
          </div>
        </Card>

        <Collapse
          ghost
          style={{ marginTop: 8 }}
          items={[
            {
              key: 'advanced',
              label: 'Advanced / provenance',
              children: (
                <>
                  <Form.Item name="rateSource" label="Rate source" initialValue="MANUAL">
                    <Select options={RATE_SOURCES.map((v) => ({ value: v, label: v }))} />
                  </Form.Item>
                  <Form.Item name="sourceRefLabel" label="Provenance label">
                    <Input placeholder="Optional source reference" />
                  </Form.Item>
                  <Form.Item name="supplierName" label="Supplier">
                    <Input placeholder="Optional supplier" />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />
      </Form>
    </Drawer>
  );
};
